import React, { useCallback, useState } from 'react';
import { isNil } from 'lodash';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { TAX_STATUS_LABELS, type TaxStatusEnum } from 'venn-api';
import { getPricePerShare } from './types';
import type { HoldingPeriodInput, LotInput } from './types';
import { Icon, SimpleDatePicker } from 'venn-ui-kit';

const CURRENCY_PLACEHOLDER = '$0.00';

const getInputClass = (hasError: boolean) =>
  cn(
    'w-[80px] h-[36px] px-[8px] border border-solid rounded text-[13px] text-venn-black font-normal placeholder:text-venn-grey-60 focus:outline-none',
    hasError ? 'border-venn-error focus:border-venn-error' : 'border-venn-grey-30 focus:border-venn-dark-blue',
  );

export const formatCurrencyDisplay = (value: number): string => {
  if (value === 0) return '';
  return `$${value.toFixed(2)}`;
};

export const parseCurrencyInput = (input: string): number => {
  const cleaned = input.replace(/[$,]/g, '');
  const parsed = parseFloat(cleaned);
  return Number.isNaN(parsed) ? 0 : Math.round(parsed * 100) / 100;
};

const TAX_STATUS_OPTIONS: TaxStatusEnum[] = ['TAXABLE', 'TAX_DEFERRED', 'TAX_EXEMPT'];

export type LotIncompleteFields = {
  marketValue: boolean;
  costBasis: boolean;
  quantity: boolean;
};

export const getLotIncompleteFields = (lot: LotInput): LotIncompleteFields | undefined => {
  if (lot.isNew) {
    return undefined;
  }
  const isFilled = lot.marketValue > 0 || lot.costBasis > 0 || lot.quantity > 0;
  if (!isFilled) {
    return undefined;
  }
  return {
    marketValue: lot.marketValue <= 0,
    costBasis: lot.costBasis <= 0,
    quantity: lot.quantity <= 0,
  };
};

export const TaxLotInputRow = ({
  lot,
  onChange,
  onRemove,
  index,
  incompleteFields,
  costPerShareInconsistent,
}: {
  lot: LotInput;
  onChange: (updated: LotInput) => void;
  onRemove: (() => void) | undefined;
  index: number;
  incompleteFields: LotIncompleteFields | undefined;
  costPerShareInconsistent: boolean;
}) => {
  const rowHasBothOrNeitherPriceInputs = lot.marketValue > 0 === lot.quantity > 0;
  const costPerShareError = costPerShareInconsistent && rowHasBothOrNeitherPriceInputs;

  const holdingType = lot.holdingPeriod.type;
  const isDateMode = holdingType === 'FromDate';
  const datePurchased = lot.holdingPeriod.type === 'FromDate' ? lot.holdingPeriod.datePurchased : undefined;
  const [lastDate, setLastDate] = useState<number | undefined>(datePurchased);
  const currentPrice = getPricePerShare(lot);

  if (!isNil(datePurchased) && datePurchased !== lastDate) {
    setLastDate(datePurchased);
  }

  const handleHoldingPeriodChange = useCallback(
    (value: string) => {
      let newHolding: HoldingPeriodInput;
      if (value === 'FromDate') {
        newHolding = { type: 'FromDate', datePurchased: lastDate };
      } else if (value === 'LongTerm') {
        newHolding = { type: 'LongTerm' };
      } else {
        newHolding = { type: 'ShortTerm' };
      }
      onChange({ ...lot, holdingPeriod: newHolding });
    },
    [lot, onChange, lastDate],
  );

  const handleDateChange = useCallback(
    (timestamp: number) => {
      onChange({ ...lot, holdingPeriod: { type: 'FromDate', datePurchased: timestamp } });
    },
    [lot, onChange],
  );

  const handleTaxStatusChange = useCallback(
    (value: string) => {
      onChange({ ...lot, taxStatus: value as TaxStatusEnum });
    },
    [lot, onChange],
  );

  const handleMarketValueBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const parsed = parseCurrencyInput(e.target.value);
      onChange({ ...lot, marketValue: parsed });
      e.target.value = formatCurrencyDisplay(parsed);
    },
    [lot, onChange],
  );

  const handleCostBasisBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const parsed = parseCurrencyInput(e.target.value);
      onChange({ ...lot, costBasis: parsed });
      e.target.value = formatCurrencyDisplay(parsed);
    },
    [lot, onChange],
  );

  const handleQuantityBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const parsed = parseInt(e.target.value, 10);
      onChange({ ...lot, quantity: Number.isNaN(parsed) ? 0 : parsed });
    },
    [lot, onChange],
  );

  return (
    <div className="flex flex-col gap-[4px]">
      <div className="flex items-end gap-[4px]">
        <div className="flex flex-col gap-[4px]">
          <span className="text-[13px] font-bold text-venn-black">Date Purchased or Holding Period</span>
          <RadioGroup
            value={holdingType}
            onValueChange={handleHoldingPeriodChange}
            className="flex items-center gap-[8px]"
          >
            <div className="flex items-center gap-[4px]">
              <RadioGroupItem
                value="FromDate"
                id={`${lot.id}-date`}
                state={isDateMode ? 'checked' : 'unchecked'}
                disabled={false}
              />
              <SimpleDatePicker value={datePurchased ?? lastDate} onChange={handleDateChange} disabled={!isDateMode} />
            </div>
            <div className="flex items-center gap-[4px] whitespace-nowrap">
              <RadioGroupItem
                value="LongTerm"
                id={`${lot.id}-long`}
                state={holdingType === 'LongTerm' ? 'checked' : 'unchecked'}
                disabled={false}
              />
              <label htmlFor={`${lot.id}-long`} className="text-[13px] font-normal text-venn-black cursor-pointer">
                Long Term
              </label>
            </div>
            <div className="flex items-center gap-[4px] whitespace-nowrap">
              <RadioGroupItem
                value="ShortTerm"
                id={`${lot.id}-short`}
                state={holdingType === 'ShortTerm' ? 'checked' : 'unchecked'}
                disabled={false}
              />
              <label htmlFor={`${lot.id}-short`} className="text-[13px] font-normal text-venn-black cursor-pointer">
                Short Term
              </label>
            </div>
          </RadioGroup>
        </div>
        <div className="flex items-end gap-[8px]">
          <div className="flex flex-col gap-[4px] ml-[20px]">
            <span className="text-[13px] font-bold text-venn-black">Tax Status</span>
            <Select value={lot.taxStatus} onValueChange={handleTaxStatusChange}>
              <SelectTrigger className="w-[160px] h-[36px] text-[13px] text-venn-black border-solid">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TAX_STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status} value={status} className="text-[13px] py-[8px]">
                    {TAX_STATUS_LABELS[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-[4px]">
            <span className="text-[13px] font-bold text-venn-black">Cost Basis</span>
            <input
              type="text"
              placeholder={CURRENCY_PLACEHOLDER}
              defaultValue={formatCurrencyDisplay(lot.costBasis)}
              onBlur={handleCostBasisBlur}
              className={getInputClass(!!incompleteFields?.costBasis)}
            />
          </div>

          <div className="flex flex-col gap-[4px]">
            <span className="text-[13px] font-bold text-venn-black">Market Value</span>
            <input
              type="text"
              placeholder={CURRENCY_PLACEHOLDER}
              defaultValue={formatCurrencyDisplay(lot.marketValue)}
              onBlur={handleMarketValueBlur}
              className={getInputClass(costPerShareError || (lot.quantity > 0 && lot.marketValue <= 0))}
            />
          </div>
        </div>
        <Icon type="divide" className="self-end mb-[10px] gap-[4px]" data-testid="qa-icon-divide" />

        <div className="flex flex-col gap-[4px]">
          <span className="text-[13px] font-bold text-venn-black">Quantity</span>
          <input
            type="text"
            placeholder="0"
            defaultValue={lot.quantity !== 0 ? String(lot.quantity) : ''}
            onBlur={handleQuantityBlur}
            className={getInputClass(costPerShareError || (lot.marketValue > 0 && lot.quantity <= 0))}
          />
        </div>

        <Icon type="equals" className="self-end mb-[10px] gap-[4px]" data-testid="qa-icon-equals" />

        <div className="flex flex-col gap-[4px]">
          <span className="text-[13px] font-bold text-venn-black">Current Price</span>
          <div
            data-testid="qa-current-price"
            className={cn(
              'w-[80px] h-[36px] px-[8px] flex items-center rounded text-[13px]',
              costPerShareInconsistent ? 'text-venn-error bg-[#F4E1E1]' : 'text-venn-grey-80 bg-venn-grey-10',
            )}
          >
            {currentPrice !== undefined ? formatCurrencyDisplay(currentPrice) || '' : ''}
          </div>
        </div>

        <div className="flex-1 flex justify-center items-center self-end h-[36px] pl-[10px]">
          {index > 0 && (
            <button
              type="button"
              onClick={onRemove}
              className="border-0 bg-transparent cursor-pointer p-0 [&_i]:text-[12px] [&_i]:text-venn-grey-60 [&_i:hover]:text-venn-error"
              data-testid="qa-remove-lot"
            >
              <Icon type="trash" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
